import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface TaskLocationEntry {
  status: 'pending' | 'assigned' | 'in_progress' | 'completed' | 'cancelled';
  timestamp: any;
  location: {
    latitude: number;
    longitude: number;
  };
  ninjaId?: string;
}

/**
 * The GPS ping recorded on each status change for one task, newest first.
 *
 * Single source of truth for `tasks/{id}.statusLocationHistory` — both
 * `TaskLocationHistory` (the audit timeline) and `TaskRouteMap` (the ninja's
 * live marker) need it, and previously only the timeline read it. Splitting
 * the fetch out means the map doesn't have to either duplicate the read logic
 * or reach into the timeline component's internals to get at the same data.
 */
export function useTaskLocationHistory(taskId: string | undefined) {
  const [history, setHistory] = useState<TaskLocationEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!taskId) {
      setHistory([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const snap = await getDoc(doc(db, 'tasks', taskId));
        if (cancelled) return;

        if (!snap.exists()) {
          setError('Task not found');
          return;
        }

        const raw: TaskLocationEntry[] = snap.data().statusLocationHistory || [];
        const sorted = [...raw].sort((a, b) => {
          const ta = a.timestamp?.toDate?.() ?? new Date(0);
          const tb = b.timestamp?.toDate?.() ?? new Date(0);
          return tb.getTime() - ta.getTime();
        });
        setHistory(sorted);
      } catch (err: any) {
        if (!cancelled) {
          console.error('Error fetching task location history:', err);
          setError(err?.message || 'Failed to load location history');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [taskId]);

  return { history, loading, error };
}
